import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Save, Plus, Trash2, Star } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import AdminLayout from '@/components/AdminLayout';

const defaultContent = {
  type: "site_content",
  hero: {
    headline: "Ihr Bauprojekt in sicheren Händen",
    subheadline: "Innenausbau, Renovierung, Fassadenarbeiten und hochwertige Bauleistungen – sauber, zuverlässig und professionell umgesetzt."
  },
  trust_bar: [
    "Kostenlos & unverbindlich",
    "Schnelle Rückmeldung",
    "Direkter WhatsApp Kontakt",
    "26x 5-Sterne Bewertungen"
  ],
  cta: {
    headline: "Lassen Sie uns Ihr Projekt besprechen",
    subheadline: "Kostenlos & unverbindlich",
    button_text: "Jetzt kostenlose Einschätzung anfordern"
  },
  reviews: {
    headline: "Was Kunden über BEKAJ BAU sagen",
    subheadline: "26 echte 5-Sterne-Bewertungen sprechen für sich.",
    rating: 5.0,
    count: 26,
    items: [
      { name: "Thomas M.", text: "BEKAJ BAU hat unsere Wohnung komplett renoviert. Saubere Arbeit, pünktliche Umsetzung und super Kommunikation.", rating: 5, active: true },
      { name: "Sandra K.", text: "Professionelle Fassadenarbeiten zu einem fairen Preis. Das Team war freundlich und hat alles termingerecht fertiggestellt.", rating: 5, active: true },
      { name: "Michael B.", text: "Herr Bekaj und sein Team haben unseren Trockenbau perfekt umgesetzt. Von der Beratung bis zur Abnahme alles top.", rating: 5, active: true },
      { name: "Petra W.", text: "Wir haben BEKAJ BAU für unsere Badsanierung beauftragt. Das Ergebnis hat unsere Erwartungen übertroffen.", rating: 5, active: true },
      { name: "Andreas H.", text: "Schnelle Rückmeldung, direkte Kommunikation per WhatsApp und ein hervorragendes Ergebnis. So muss Handwerk sein!", rating: 5, active: true },
      { name: "Julia R.", text: "Kompletter Innenausbau in Rekordzeit. Die Qualität stimmt, der Preis ist fair. Wir empfehlen BEKAJ BAU uneingeschränkt weiter.", rating: 5, active: true }
    ]
  },
  video: {
    active: false,
    url: "",
    headline: "Einblick in unsere Arbeit",
    subheadline: "Sehen Sie selbst, wie professionell wir Projekte umsetzen."
  },
  services: [
    { title: "Innenausbau", description: "Kompletter Innenausbau von der Planung bis zur Fertigstellung", active: true },
    { title: "Fassadenarbeiten", description: "Fassadensanierung, Dämmung und Anstrich für ein neues Erscheinungsbild", active: true },
    { title: "Renovierung & Sanierung", description: "Modernisierung und fachgerechte Sanierung von Bestandsimmobilien", active: true },
    { title: "Trockenbau", description: "Präzise Trockenbauarbeiten für Wände, Decken und Raumgestaltung", active: true },
    { title: "Fliesenarbeiten", description: "Professionelle Fliesenverlegung für Bad, Küche und Wohnräume", active: true },
    { title: "Bodenbeschichtungen", description: "Hochwertige Bodenbeschichtungen für jeden Einsatzbereich", active: true },
    { title: "Malerarbeiten", description: "Perfekte Oberflächen und strahlende Farben – innen wie außen", active: true },
    { title: "Komplettlösungen", description: "Alles aus einer Hand – von der Beratung bis zur Abnahme", active: true }
  ]
};

const AdminContent = () => {
  const [content, setContent] = useState(defaultContent);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('hero');

  const authHeader = {}; // CRP-011: Cookie-Auth — kein Bearer-Token mehr nötig

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await api.get('/content');
        if (res.data && res.data.hero) {
          setContent({ ...defaultContent, ...res.data });
        }
      } catch (err) {
        console.error('Failed to load content');
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const saveContent = async () => {
    setSaving(true);
    try {
      await api.put('/content', content, authHeader);
      toast.success('Inhalte gespeichert!');
    } catch (err) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (section, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const tabs = [
    { id: 'hero', label: 'Hero' },
    { id: 'cta', label: 'CTA Texte' },
    { id: 'reviews', label: 'Bewertungen' },
    { id: 'video', label: 'Video' },
    { id: 'services', label: 'Leistungen' },
  ];

  if (loading) {
    return <AdminLayout title="Inhalte verwalten"><div className="text-center py-12 text-gray-500">Wird geladen...</div></AdminLayout>;
  }

  return (
    <AdminLayout title="Inhalte verwalten">
      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-white rounded-lg p-1 border shadow-sm overflow-x-auto" data-testid="content-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap transition-colors ${
              activeTab === tab.id ? 'bg-[#4A331D] text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg border shadow-sm p-6">
        {/* Hero Tab */}
        {activeTab === 'hero' && (
          <div className="space-y-5" data-testid="content-hero">
            <h3 className="font-semibold text-[#4A331D]">Hero Section</h3>
            <div>
              <Label>Headline</Label>
              <Input
                value={content.hero.headline}
                onChange={(e) => updateField('hero', 'headline', e.target.value)}
                className="mt-1"
                data-testid="hero-headline-input"
              />
            </div>
            <div>
              <Label>Subheadline</Label>
              <Textarea
                value={content.hero.subheadline}
                onChange={(e) => updateField('hero', 'subheadline', e.target.value)}
                rows={3}
                className="mt-1"
                data-testid="hero-subheadline-input"
              />
            </div>
            <div>
              <Label>Trust Bar Texte (4 Punkte)</Label>
              <div className="space-y-2 mt-2">
                {content.trust_bar.map((text, idx) => (
                  <Input
                    key={idx}
                    value={text}
                    onChange={(e) => {
                      const updated = [...content.trust_bar];
                      updated[idx] = e.target.value;
                      setContent(prev => ({ ...prev, trust_bar: updated }));
                    }}
                    className="text-sm"
                    data-testid={`trust-bar-input-${idx}`}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* CTA Tab */}
        {activeTab === 'cta' && (
          <div className="space-y-5" data-testid="content-cta">
            <h3 className="font-semibold text-[#4A331D]">Call-to-Action Texte</h3>
            <div>
              <Label>CTA Headline</Label>
              <Input
                value={content.cta.headline}
                onChange={(e) => updateField('cta', 'headline', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label>CTA Subheadline</Label>
              <Input
                value={content.cta.subheadline}
                onChange={(e) => updateField('cta', 'subheadline', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Button Text</Label>
              <Input
                value={content.cta.button_text}
                onChange={(e) => updateField('cta', 'button_text', e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="space-y-5" data-testid="content-reviews">
            <h3 className="font-semibold text-[#4A331D]">Bewertungen</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Headline</Label>
                <Input
                  value={content.reviews.headline}
                  onChange={(e) => updateField('reviews', 'headline', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Subheadline</Label>
                <Input
                  value={content.reviews.subheadline}
                  onChange={(e) => updateField('reviews', 'subheadline', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Gesamtbewertung</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="1"
                  max="5"
                  value={content.reviews.rating}
                  onChange={(e) => updateField('reviews', 'rating', parseFloat(e.target.value))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Anzahl Bewertungen</Label>
                <Input
                  type="number"
                  value={content.reviews.count}
                  onChange={(e) => updateField('reviews', 'count', parseInt(e.target.value))}
                  className="mt-1"
                  data-testid="review-count-input"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-3">
                <Label>Einzelne Bewertungen</Label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const updated = { ...content, reviews: { ...content.reviews, items: [...content.reviews.items, { name: "", text: "", rating: 5, active: true }] } };
                    setContent(updated);
                  }}
                >
                  <Plus className="w-3 h-3 mr-1" /> Hinzufügen
                </Button>
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {content.reviews.items.map((review, idx) => (
                  <div key={idx} className="border rounded-lg p-3 bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <Star className="w-4 h-4 text-[#FBBC04] fill-[#FBBC04]" />
                        <Input
                          value={review.name}
                          onChange={(e) => {
                            const items = [...content.reviews.items];
                            items[idx] = { ...items[idx], name: e.target.value };
                            setContent(prev => ({ ...prev, reviews: { ...prev.reviews, items } }));
                          }}
                          placeholder="Kundenname"
                          className="h-8 w-40 text-sm"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={review.active}
                          onCheckedChange={(checked) => {
                            const items = [...content.reviews.items];
                            items[idx] = { ...items[idx], active: checked };
                            setContent(prev => ({ ...prev, reviews: { ...prev.reviews, items } }));
                          }}
                        />
                        <button
                          onClick={() => {
                            const items = content.reviews.items.filter((_, i) => i !== idx);
                            setContent(prev => ({ ...prev, reviews: { ...prev.reviews, items } }));
                          }}
                          className="p-1 text-red-400 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <Textarea
                      value={review.text}
                      onChange={(e) => {
                        const items = [...content.reviews.items];
                        items[idx] = { ...items[idx], text: e.target.value };
                        setContent(prev => ({ ...prev, reviews: { ...prev.reviews, items } }));
                      }}
                      placeholder="Bewertungstext"
                      rows={2}
                      className="text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Video Tab */}
        {activeTab === 'video' && (
          <div className="space-y-5" data-testid="content-video">
            <h3 className="font-semibold text-[#4A331D]">Video Section</h3>
            <div className="flex items-center gap-3">
              <Switch
                checked={content.video.active}
                onCheckedChange={(checked) => updateField('video', 'active', checked)}
                data-testid="video-active-switch"
              />
              <Label>Video Section aktiv</Label>
            </div>
            <div>
              <Label>Video URL (YouTube oder Vimeo)</Label>
              <Input
                value={content.video.url}
                onChange={(e) => updateField('video', 'url', e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                className="mt-1"
                data-testid="video-url-input"
              />
              <p className="text-xs text-gray-500 mt-1">Unterstützt: YouTube und Vimeo Links</p>
            </div>
            <div>
              <Label>Headline</Label>
              <Input
                value={content.video.headline}
                onChange={(e) => updateField('video', 'headline', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Subheadline</Label>
              <Input
                value={content.video.subheadline}
                onChange={(e) => updateField('video', 'subheadline', e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="space-y-5" data-testid="content-services">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-[#4A331D]">Leistungen</h3>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setContent(prev => ({
                    ...prev,
                    services: [...prev.services, { title: "", description: "", active: true }]
                  }));
                }}
              >
                <Plus className="w-3 h-3 mr-1" /> Hinzufügen
              </Button>
            </div>
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {content.services.map((service, idx) => (
                <div key={idx} className="border rounded-lg p-3 bg-gray-50">
                  <div className="flex justify-between items-center mb-2">
                    <Input
                      value={service.title}
                      onChange={(e) => {
                        const services = [...content.services];
                        services[idx] = { ...services[idx], title: e.target.value };
                        setContent(prev => ({ ...prev, services }));
                      }}
                      placeholder="Leistungsname"
                      className="h-8 w-48 text-sm font-medium"
                    />
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={service.active}
                        onCheckedChange={(checked) => {
                          const services = [...content.services];
                          services[idx] = { ...services[idx], active: checked };
                          setContent(prev => ({ ...prev, services }));
                        }}
                      />
                      <button
                        onClick={() => {
                          setContent(prev => ({
                            ...prev,
                            services: prev.services.filter((_, i) => i !== idx)
                          }));
                        }}
                        className="p-1 text-red-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <Input
                    value={service.description}
                    onChange={(e) => {
                      const services = [...content.services];
                      services[idx] = { ...services[idx], description: e.target.value };
                      setContent(prev => ({ ...prev, services }));
                    }}
                    placeholder="Beschreibung"
                    className="text-sm"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="mt-6 flex justify-end">
        <Button
          onClick={saveContent}
          disabled={saving}
          className="bg-[#4A331D] hover:bg-[#593B23] px-8"
          data-testid="save-content-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Wird gespeichert...' : 'Änderungen speichern'}
        </Button>
      </div>
    </AdminLayout>
  );
};

export default AdminContent;
